import React, { createContext, useContext, useEffect, useState } from 'react';
import userService from '../service/serviceUser';

/**
 * @function PermissionsContext
 * @description Contexto para as permissões
 * @returns {Object} - Objeto com as permissões
 */
export const PermissionsContext = createContext({});

/**
 * @function PermissionsProvider
 * @description Provider para as permissões
 * @param {Object} children - Filhos do provider
 * @returns {Object} - Objeto com as permissões
 */
export const PermissionsProvider = ({ children }) => {
  // Inicialização síncrona das permissões do localStorage
  const initializePermissions = () => {
    try {
      const storagedUser = localStorage.getItem('user_token');
      
      if (storagedUser) {
        const user = JSON.parse(storagedUser);
        return user.roles || [];
      }
    } catch (error) {
      console.error("Erro ao carregar permissões do localStorage:", error);
    }
    return [];
  };

  const [permissions, setPermissions] = useState(initializePermissions);
  const [isLoading, setIsLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  console.log("🔍 [PermissionsContext] Estado inicial:", {
    permissions: permissions.length,
    isLoading,
    lastRefresh
  });

  /**
   * @function fetchPermissions
   * @description Função para buscar as permissões
   */
  const fetchPermissions = async () => {
    if (isLoading) {
      return { success: false, error: 'Já está carregando' };
    }

    // Verifica se há token antes de fazer requisição
    const userToken = localStorage.getItem('user_token');
    if (!userToken) {
      return { success: false, error: 'Usuário não autenticado' };
    }

    try {
      setIsLoading(true);
      const retorno = await userService.getCurrentUser();
      
      if (retorno.success) {
        const storagedUser = localStorage.getItem('user_token');
        const user = JSON.parse(storagedUser || '{}');
        
        user.roles = retorno.data.roles || [];
        localStorage.setItem('user_token', JSON.stringify({
          roles: retorno.data.roles,
          ...user
        }));
        const roles = retorno.data.roles || [];

        setPermissions(roles);
        setLastRefresh(new Date());
        setRefreshTrigger(prev => prev + 1);
        setIsLoading(false);
        return { success: true, permissions: roles };
      } else {
        setIsLoading(false);
        return { success: false, error: retorno.message };
      }
    } catch (error) {
      setIsLoading(false);
      return { success: false, error: error.message };
    }
  };

  useEffect(() => {
    // Só busca permissões se houver um token de usuário (usuário logado)
    const userToken = localStorage.getItem('user_token');
    if (permissions.length === 0 && userToken) {
      fetchPermissions();
    }
  }, []);

  /**
   * @function hasPermission
   * @description Função para verificar se o usuário tem uma permissão
   * @param {string} perm - Permissão a ser verificada
   * @returns {boolean} - true se o usuário tem a permissão, false caso contrário
   */
  const hasPermission = (perm) => {
    const role = permissions.find(role => role.name === perm);
    return !!role;
  };

  /**
   * @function hasPermissionAction
   * @description Função para verificar se o usuário tem uma ação específica de uma permissão
   * @param {string} perm - Permissão a ser verificada
   * @param {string} action - Ação específica (isinsert, islist, etc.)
   * @returns {boolean} - true se o usuário tem a permissão, false caso contrário
   */
  const hasPermissionAction = (perm, action = 'islist') => {
    const role = permissions.find(role => role.name === perm);
    const result = role && role[action] === true;
    
    // console.log("🔍 [hasPermissionAction] Verificando:", {
    //   permission: perm,
    //   action,
    //   role: role ? "FOUND" : "NOT_FOUND",
    //   roleValue: role ? role[action] : "N/A",
    //   result,
    //   totalPermissions: permissions.length,
    //   lastRefresh
    // });
    
    return result;
  };

  /**
   * @function getPermission
   * @description Função para obter uma permissão específica
   * @param {string} perm - Permissão a ser obtida
   * @returns {Object|null} - Objeto da permissão ou null
   */
  const getPermission = (perm) => {
    return permissions.find(role => role.name === perm) || null;
  };

  /**
   * @function refreshPermissions
   * @description Função para atualizar as permissões
   */
  const refreshPermissions = async () => {
    if (!isLoading) {
      return await fetchPermissions();
    }
    return { success: false, error: 'Já está carregando as permissões' };
  };

  /**
   * @function clearCacheAndRefresh
   * @description Limpa o cache do localStorage e força uma busca fresca das permissões
   */
  const clearCacheAndRefresh = async () => {
    try {
      const storagedUser = localStorage.getItem('user_token');
      if (!storagedUser) {
        return { success: false, error: 'Usuário não autenticado' };
      }

      const user = JSON.parse(storagedUser);
      delete user.roles;
      localStorage.setItem('user_token', JSON.stringify(user));
      
      return await fetchPermissions();
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const value = React.useMemo(() => ({
    permissions,
    refreshPermissions,
    clearCacheAndRefresh,
    hasPermission,
    hasPermissionAction,
    getPermission,
    isLoading,
    lastRefresh,
    refreshTrigger
  }), [permissions, isLoading, lastRefresh, refreshTrigger]);

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

/**
 * @function usePermission
 * @description Hook para usar as permissões
 * @returns {Object} - Objeto com as permissões
 */
export const usePermissions = () => {
  const ctx = useContext(PermissionsContext);
  if (!ctx) throw new Error('usePermission must be used within PermissionProvider');
  return ctx;
};